import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AiOutlineSearch } from 'react-icons/ai';
import { FaPlus } from 'react-icons/fa';
import { Button, InputGroup, Form } from 'react-bootstrap';

import Wrapper from '../../components/Wrapper';
import SupportPagination from '../../components/utils/supportPagination'; 
import PartnerPagination from '../../components/utils/partnerPagination'; 
import FarmTable from '../../components/tables/FarmTable';
import { hasPermission } from '../../hooks/usePermission';
import { getRole } from '../../utils/role';
import axiosInstance from '../../utils/axios';

const SUPPORT_LIMIT = 15;

const Farms = () => {
  const navigate = useNavigate();

  const role = getRole();
  const isClientSide = role === 'FIELD_OFFICER' || role === 'FARMER' || role === 'USER';

  const [searchTerm, setSearchTerm] = useState('');
  const [submittedSearch, setSubmittedSearch] = useState('');
  
  const [allFarms, setAllFarms] = useState([]);
  const [farms, setFarms] = useState([]);
  const [status, setStatus] = useState('loading');
  
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [clientPage, setClientPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5); 

  const loadData = useCallback(
    async (pageToFetch = currentPage, keyword = submittedSearch) => {
      setStatus('loading');

      try {
        let url = '';
        let params = {};

        if (role === 'SUPPORT') {
          url = '/super-admin/farms';
          params = {
            page: pageToFetch,
            limit: SUPPORT_LIMIT,
            keyword: keyword || undefined,
          };
        } else if ((role === 'PARTNER' || role === 'TEAM_MEMBER')|| role === 'ADMIN') {
          url = '/admin/farms';
          params = {
            page: pageToFetch,
            limit: SUPPORT_LIMIT,
            keyword: keyword || undefined,
          };
        } else if (role === 'FIELD_OFFICER') {
          url = '/field-officer/farms';
        } else if (role === 'FARMER' || role === 'USER') {
          url = '/user/farms';
        }

        const response = await axiosInstance.get(url, { params });

        if (isClientSide) {
          setAllFarms(response.data.data || []);
        } else {
          setFarms(response.data.data || []);

          if (response.data.pagination) {
            setTotalPages(response.data.pagination.totalPages);
            setCurrentPage(response.data.pagination.currentPage);
          }
        }
      } catch (error) {
        console.error('Error fetching farms:', error);
      } finally {
        setStatus('idle');
      }
    },
    [currentPage, submittedSearch, role, isClientSide]
  );

  useEffect(() => {
    window.scrollTo(0, 0);
    loadData();
  }, [submittedSearch, loadData]); 

  const handleSearch = () => {
    if (isClientSide) return;
    setSubmittedSearch(searchTerm.trim());
    if (currentPage !== 1) {
      setCurrentPage(1);
      loadData(1, searchTerm.trim());
    }
  };

  const filteredData =
    isClientSide && Array.isArray(allFarms)
      ? allFarms.filter((item) =>
          item &&
          Object.values(item).some(
            (value) =>
              value != null &&
              (typeof value === 'string' || typeof value === 'number') &&
              value.toString().toLowerCase().includes(searchTerm.toLowerCase())
          )
        )
      : [];

  let tableData;
  let startIndex;
  let totalClientPages;

  if (isClientSide) {
    totalClientPages = Math.ceil(filteredData.length / itemsPerPage);
    const indexOfFirst = (clientPage - 1) * itemsPerPage;
    tableData = filteredData.slice(indexOfFirst, indexOfFirst + itemsPerPage);
    startIndex = indexOfFirst;
  } else {
    tableData = farms;
    startIndex = (currentPage - 1) * SUPPORT_LIMIT;
  }

  const reduxCompatibleSetCurrentPage = (pageNum) => {
    setCurrentPage(pageNum);
    return { type: 'DUMMY_SET_PAGE', payload: pageNum };
  };

  const reduxCompatibleFetchData = (params) => {
    loadData(params.page, params.keyword); 
    return { type: 'DUMMY_FETCH_DATA', payload: params };
  };

  return (
    <Wrapper>
      <div className='reports-container py-4'>
        <div className='d-flex flex-column flex-md-row justify-content-between mb-3 w-100'>
          <h1 className='fw-bolder'>Farms</h1>

          <div className='d-flex justify-content-end align-items-center mt-3 m-md-0 gap-2'>
            <InputGroup>
              <Form.Control
                type='text'
                className='bg-light border-secondary'
                placeholder='Search Farms'
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  if (isClientSide) setClientPage(1);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSearch();
                }}
              />
              {!isClientSide && (
                <Button variant='outline-secondary' onClick={handleSearch} title='Search'>
                  <AiOutlineSearch />
                </Button>
              )}
            </InputGroup>

            {hasPermission('farms:create') && (
              <button
                className='btn btn-primary h-100 d-flex align-items-center text-nowrap'
                title='Add Farm'
                onClick={() => navigate('/farms/add')}
              >
                <FaPlus />
                <span className='d-none d-md-inline ms-2'>Add Farm</span>
              </button>
            )}
          </div>
        </div>

        {status === 'loading' ? (
          <div className="d-flex justify-content-center py-5">
            <h4 className='text-muted'>Loading farms...</h4>
          </div>
        ) : (
          <>
            <FarmTable farms={tableData} startIndex={startIndex} />

            {isClientSide ? (
              <PartnerPagination
                currentPage={clientPage}
                totalPages={totalClientPages}
                onPageChange={setClientPage}
                onItemsPerPageChange={(newLimit) => {
                  setItemsPerPage(newLimit);
                  setClientPage(1);
                }}
              />
            ) : (
              <SupportPagination
                currentPage={currentPage}
                totalPages={totalPages}
                setCurrentPage={reduxCompatibleSetCurrentPage}
                fetchData={reduxCompatibleFetchData}
                searchKeyword={submittedSearch}
              />
            )}
          </>
        )}
      </div>
    </Wrapper>
  );
};

export default Farms;